import { Component } from '@angular/core';
import { WatchAccountService } from '../../../../services/watch-account.service';
import { AccountService } from '../../../../services/account.service';
import { Subscription } from 'rxjs';
import { BsModalRef, ModalOptions, BsModalService } from 'ngx-bootstrap/modal';
import { WatchOrganizationService } from '../../../../services/watch-organization.service';
import { ModalConfirmDeleteAccountComponent } from '../../../modals/confirm-delete-account/modal-confirm-delete-account.component';
import { Router } from '@angular/router';
import { ITimeZone } from '../../../../interfaces/graph-types';
import { UtilityService } from '../../../../services/utility.service';

@Component({
  selector: '[account-settings]',
  template: require('./account-settings.component.pug'),
  styles: [require('./account-settings.component.css').toString()],
})
export class AccountSettingsComponent {
  orgId = null;
  accountName = '';
  customDomainTracking = '';
  trackingDelay = null;
  sub: Subscription = null;
  loading = true;
  bsModalRef: BsModalRef;
  timeZoneSub: Subscription;
  timeZones: ITimeZone[];
  timeZone: string;

  openNotification: boolean;
  openSound: boolean;
  clickNotification: boolean;
  clickSound: boolean;
  colleaguesShareRole: boolean;

  labelColleague =
    'By default, using {{=colleagues}} attribute will reference all candidates in the same company. If you enable this, colleagues will also need to have the same value for the field Role to be considered colleagues. Note: If the candidate has no value for the Role and this setting is enabled, no colleagues will be found.';

  // -------------------------------------------------------------------
  constructor(
    private watchAcccount: WatchAccountService,
    private accountService: AccountService,
    private modalService: BsModalService,
    private utilityService: UtilityService,
    private router: Router,
  ) {
    this.timeZones = [];
    this.timeZoneSub = this.utilityService.getTimeZones().subscribe(res => {
      this.timeZones = res.timeZones;
    });
  }

  // -------------------------------------------------------------------
  ngOnInit() {
    this.loading = true;
    this.sub = this.accountService
      .getAccountInfo(this.watchAcccount.accountId().getValue())
      .subscribe(account => {
        this.accountName = account.name;
        this.customDomainTracking = account.customDomainTracking;
        this.trackingDelay = account.trackingDelayInSec;
        this.timeZone = account.timeZone;
        this.loading = false;
        this.orgId = account.organization.id;
        this.openNotification = account.openNotification;
        this.openSound = account.openSound;
        this.clickNotification = account.clickNotification;
        this.clickSound = account.clickSound;
        this.colleaguesShareRole = account.colleaguesShareRole;
      });
  }

  // -------------------------------------------------------------------
  ngOnDestroy() {
    if (this.timeZoneSub) {
      this.timeZoneSub.unsubscribe();
    }
    if (this.sub) {
      this.sub.unsubscribe();
    }
  }

  // -------------------------------------------------------------------
  confirmDestroyAccount(
    data: any,
    onsuccess: onSmartSuccess,
    onfailure: onSmartFailure,
  ) {
    //this.account_service.account().subscribe(response => {
    const options: ModalOptions = new ModalOptions();
    options.class = 'gray modal-lg';
    options.ignoreBackdropClick = true;
    options.initialState = {
      confirmName: this.accountName,
    };
    this.bsModalRef = this.modalService.show(
      ModalConfirmDeleteAccountComponent,
      options,
    );
    this.bsModalRef.content.closeBtnName = 'Close';

    let sub = this.bsModalRef.content.reason.subscribe(res => {
      console.error('DESTROY ACCOUNT');
      sub.unsubscribe();
      if (res) {
        this.router.navigate(['/org/' + this.orgId]);
      } else {
        onsuccess();
      }
    });
  }

  timeZoneErrorText = '';
  // -------------------------------------------------------------------
  changeTimeZone(value) {
    this.timeZoneErrorText = '';

    this.changeSettingDetected(
      'time_zone',
      value,
      () => {},
      error => {
        this.timeZoneErrorText = error;
      },
    );
  }

  // -------------------------------------------------------------------
  changeSettingDetected(
    data: any,
    newValue: any,
    onsuccess: onSmartSuccess,
    onfailure: onSmartFailure,
  ) {
    this.accountService.setSettings(
      this.watchAcccount.accountId().getValue(),
      data,
      newValue,
      () => {
        onsuccess();
      },
      error => {
        onfailure(error);
      },
    );
  }

  // -------------------------------------------------------------------
  setSettings(
    name: string,
    value: boolean,
    onsuccess: onSmartSuccess,
    onfailure: onSmartFailure,
  ) {
    this.accountService.setSettings(
      this.watchAcccount.accountId().getValue(),
      name,
      value.toString(),
      result => {
        onsuccess();
      },
      error => {
        onfailure(error);
      },
    );
  }

  // -------------------------------------------------------------------
  setOpenNotification(
    data: any,
    onsuccess: onSmartSuccess,
    onfailure: onSmartFailure,
  ) {
    this.setSettings(
      'open_notification',
      !this.openNotification,
      () => {
        this.openNotification = !this.openNotification;
        onsuccess();
      },
      error => {
        onfailure(error);
      },
    );
  }

  // -------------------------------------------------------------------
  setOpenSound(
    data: any,
    onsuccess: onSmartSuccess,
    onfailure: onSmartFailure,
  ) {
    this.setSettings(
      'open_sound',
      !this.openSound,
      () => {
        this.openSound = !this.openSound;
        onsuccess();
      },
      error => {
        onfailure(error);
      },
    );
  }

  // -------------------------------------------------------------------
  setClickNotification(
    data: any,
    onsuccess: onSmartSuccess,
    onfailure: onSmartFailure,
  ) {
    this.setSettings(
      'click_notification',
      !this.clickNotification,
      () => {
        this.clickNotification = !this.clickNotification;
        onsuccess();
      },
      error => {
        onfailure(error);
      },
    );
  }

  // -------------------------------------------------------------------
  setClickSound(
    data: any,
    onsuccess: onSmartSuccess,
    onfailure: onSmartFailure,
  ) {
    this.setSettings(
      'click_sound',
      !this.clickSound,
      () => {
        this.clickSound = !this.clickSound;
        onsuccess();
      },
      error => {
        onfailure(error);
      },
    );
  }

  // -------------------------------------------------------------------
  setColleaguesShareRole(
    data: any,
    onsuccess: onSmartSuccess,
    onfailure: onSmartFailure,
  ) {
    this.setSettings(
      'colleagues_share_role',
      !this.colleaguesShareRole,
      () => {
        this.colleaguesShareRole = !this.colleaguesShareRole;
        onsuccess();
      },
      error => {
        onfailure(error);
      },
    );
  }
}
